import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
	Grid, Col, Nav, NavItem, Tab,
} from 'react-bootstrap';
import { isEqual, isEmpty, pick } from 'lodash';
import { Loader, SubNavBar } from '@components/common';
import Actions, { thunks } from '@redux/locations';
import { DetailsJobList as JobList } from '@components';
import MapPanel from './components/mapPanel';
import OfficersList from './components/officersList';
import { jobColumns } from './utils/columnsDefinition';

class LocationView extends Component {
	componentDidMount = () => {
		const {
			getLocationDetails, getJobsData, getOfficers,
		} = this.props;
		getLocationDetails();
		getJobsData('activeJobs');
		getJobsData('jobHistory');
		getOfficers();
	}

	shouldComponentUpdate = ({
		loading,
		location,
		activeJobCount,
		completedJobCount,
		activeJobsData,
		activeJobsTotalCount,
		activeJobsFilters,
		jobHistoryData,
		jobHistoryTotalCount,
		jobHistoryFilters,
	}) => {
		const lastProps = this.props;

		return (
			!isEqual(loading, lastProps.loading)
			|| !isEqual(location, lastProps.location)
			|| !isEqual(activeJobCount, lastProps.activeJobCount)
			|| !isEqual(completedJobCount, lastProps.completedJobCount)
			|| !isEqual(activeJobsData, lastProps.activeJobsData)
			|| !isEqual(activeJobsTotalCount, lastProps.activeJobsTotalCount)
			|| !isEqual(activeJobsFilters, lastProps.activeJobsFilters)
			|| !isEqual(jobHistoryData, lastProps.jobHistoryData)
			|| !isEqual(jobHistoryTotalCount, lastProps.jobHistoryTotalCount)
			|| !isEqual(jobHistoryFilters, lastProps.jobHistoryFilters)
		);
	}

	handleOnClickBack = (event) => {
		event.preventDefault();
		const { history } = this.props;

		history.goBack();
	}

	jobsOnPaginate = (pageNumber, type) => {
		const {
			getJobsData,
			updateJobListFilters,
		} = this.props;

		updateJobListFilters(type, { pageNumber });

		getJobsData(type);
	}

	render() {
		const {
			loading,
			location,
			activeJobCount,
			completedJobCount,
			activeJobsData,
			activeJobsTotalCount,
			activeJobsFilters,
			updateJobListFilters,
			jobHistoryData,
			jobHistoryTotalCount,
			jobHistoryFilters,
			TimeZoneId,
		} = this.props;

		if (loading.data || isEmpty(location)) return <Loader active />;

		return (
			<Fragment>
				<SubNavBar
					backText="Back to locations list"
					backButton
					onClickBack={this.handleOnClickBack}
				/>
				<Grid className="margin-top-20 margin-bottom-20">
					<Col
						md={12}
						sm={12}
						xs={12}
					>
						<span className="color-dark font-light font-size-38">Location Details</span>
					</Col>
				</Grid>
				<Grid>
					<MapPanel location={location} />
					<Col md={8}>
						<Tab.Container
							id="tabs"
							defaultActiveKey="active"
						>
							<div className="bg-white margin-top-10-mob">
								<Nav bsStyle="tabs">
									<NavItem
										key="active"
										eventKey="active"
									>
										Active Jobs (
										{activeJobCount || 0}
										)
									</NavItem>
									<NavItem
										key="history"
										eventKey="history"
									>
										Job History (
										{completedJobCount || 0}
										)
									</NavItem>
									<NavItem
										key="officers"
										eventKey="officers"
									>
										Officers
									</NavItem>
								</Nav>
								<Tab.Content animation>
									<Tab.Pane
										eventKey="active"
										key="active"
									>
										<JobList
											type="activeJobs"
											jobColumns={() => jobColumns(TimeZoneId)}
											data={activeJobsData}
											totalCount={activeJobsTotalCount}
											filters={activeJobsFilters}
											loading={loading.activeJobs}
											onPaginate={this.jobsOnPaginate}
										/>
									</Tab.Pane>
									<Tab.Pane
										eventKey="history"
										key="history"
									>
										<JobList
											type="jobHistory"
											jobColumns={() => jobColumns(TimeZoneId)}
											data={jobHistoryData}
											totalCount={jobHistoryTotalCount}
											filters={jobHistoryFilters}
											loading={loading.jobHistory}
											onPaginate={this.jobsOnPaginate}
											resetJobHistoryFilters={() => updateJobListFilters('jobHistory', { pageNumber : 1 })}
										/>
									</Tab.Pane>
									<Tab.Pane
										eventKey="officers"
										key="officers"
									>
										<OfficersList />
									</Tab.Pane>
								</Tab.Content>
							</div>
						</Tab.Container>
					</Col>
				</Grid>
			</Fragment>
		);
	}
}

const mapStateToProps = ({
	locations : {
		details : {
			data       : location,
			activeJobs : {
				data       : activeJobsData,
				totalCount : activeJobsTotalCount,
				filters    : activeJobsFilters,
				totalCount : activeJobCount,
			},
			jobHistory : {
				data       : jobHistoryData,
				totalCount : jobHistoryTotalCount,
				filters    : jobHistoryFilters,
				totalCount : completedJobCount,
			},
		},
		loading,
	}, app: { user: { TimeZoneId } },
}) => ({
	location,
	loading,
	activeJobCount,
	completedJobCount,
	activeJobsData,
	activeJobsTotalCount,
	activeJobsFilters,
	jobHistoryData,
	jobHistoryTotalCount,
	jobHistoryFilters,
	TimeZoneId,
});

const mapDispatchToProps = (dispatch) => ({
	updateJobListFilters : bindActionCreators(Actions, dispatch).updateSectionFilters,
	...pick(bindActionCreators(thunks, dispatch), ['getLocationDetails', 'getJobsData', 'getOfficers']),
});

LocationView.propTypes = {
	TimeZoneId : PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]).isRequired,
	activeJobCount       : PropTypes.number,
	activeJobsData       : PropTypes.array,
	activeJobsFilters    : PropTypes.object,
	activeJobsTotalCount : PropTypes.number,
	completedJobCount    : PropTypes.number,
	getLocationDetails   : PropTypes.func.isRequired,
	getJobsData          : PropTypes.func.isRequired,
	getOfficers          : PropTypes.func.isRequired,
	history              : PropTypes.object.isRequired,
	jobHistoryData       : PropTypes.array,
	jobHistoryFilters    : PropTypes.object,
	jobHistoryTotalCount : PropTypes.number,
	loading              : PropTypes.object.isRequired,
	location             : PropTypes.object,
	updateJobListFilters : PropTypes.func.isRequired,
};

LocationView.defaultProps = {
	activeJobCount       : 0,
	activeJobsData       : [],
	activeJobsFilters    : {},
	activeJobsTotalCount : 0,
	completedJobCount    : 0,
	jobHistoryData       : [],
	jobHistoryFilters    : {},
	jobHistoryTotalCount : 0,
	location             : {},
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationView);
